import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PdfExporter } from 'common/types/pdfExport';
import {
  emitThrottleEnd,
  emitThrottleStart,
  resetThrottleState,
  subscribeThrottleEvents,
  THROTTLE_MESSAGE_KIND,
  ThrottleMessage,
} from 'dashboard409a/services/throttleSignal';
import { Logger } from 'utilities';
import { PdfExportProgressDialog } from './components/PdfExportProgressDialog';
import { runCapturePipeline } from './orchestrator';
import { ExportProgressState, PageResult, PdfExportInput } from './types';

interface PdfExportContextValue {
  state: ExportProgressState;
  start: (input: PdfExportInput) => void;
  cancel: () => void;
}

const PdfExportContext = createContext<PdfExportContextValue | null>(null);

const buildInitialState = (pages: PdfExportInput['pages']): ExportProgressState => ({
  status: 'idle',
  pages: pages.map((p, index) => ({
    index,
    title: p.title,
    exportTarget: p.exportTarget,
    url: p.url,
    status: 'pending',
  })),
});

const triggerBrowserDownload = (blob: Blob, filename: string) => {
  const safeName = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const PdfExportProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<ExportProgressState>({ status: 'idle', pages: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const configRef = useRef<PdfExportInput | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const filenameRef = useRef<string>('pdf-export');
  const throttleSourcesRef = useRef<Set<Window>>(new Set());

  // Mirror the global throttle signal into export state so the progress dialog
  // can explain a quiet pipeline when 429 Retry-After waits are in flight.
  useEffect(() => subscribeThrottleEvents(({ active }) => {
    setState(prev => (prev.isThrottled === active ? prev : { ...prev, isThrottled: active }));
  }), []);

  // Capture iframes emit throttle transitions in their own window scope; relay
  // each iframe's local 0→1 / 1→0 transition into the parent throttle signal so
  // the dialog can react. Source-tracking keeps the parent's count accurate when
  // several iframes throttle in parallel.
  useEffect(() => {
    const activeIframes = throttleSourcesRef.current;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      const data = event.data as ThrottleMessage | undefined;
      if (!data || data.kind !== THROTTLE_MESSAGE_KIND) {
        return;
      }
      const source = event.source as Window | null;
      if (!source) {
        return;
      }
      const wasPresent = activeIframes.has(source);
      if (data.active && !wasPresent) {
        activeIframes.add(source);
        emitThrottleStart();
      } else if (!data.active && wasPresent) {
        activeIframes.delete(source);
        emitThrottleEnd();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setState({ status: 'idle', pages: [] });
    blobRef.current = null;
    configRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  const start = useCallback((input: PdfExportInput) => {
    if (!input.pages || input.pages.length === 0) {
      Logger.warn('PdfExport: start() called with no pages');
      return;
    }

    throttleSourcesRef.current.clear();
    resetThrottleState();
    Logger.info('PdfExport: starting export with config', input);
    Logger.info(
      `PdfExport: ${input.pages.length} page(s) queued`,
      input.pages.map((p, i) => ({
        index: i,
        title: p.title,
        exportTarget: p.exportTarget,
        exportTab: p.exportTab,
        url: p.url,
      }))
    );

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    configRef.current = input;
    filenameRef.current = input.filename || 'pdf-export';
    blobRef.current = null;

    setDialogOpen(true);
    setState({
      ...buildInitialState(input.pages),
      status: 'running',
      startedAt: performance.now(),
    });

    // Streaming PDF assembly: as each capture completes, immediately add it
    // to the PDF (in input order) and drop the Blob so the browser can GC it.
    // This caps peak memory at ~1 captured page in flight, instead of holding
    // every encoded image until the whole pipeline finishes — the latter is
    // what previously triggered Chrome to throttle later iframes once total
    // image data accumulated past ~12 captures.
    const exporter = new PdfExporter();
    const initPromise = exporter.initDocument(input).catch(err => {
      Logger.error('PdfExport: initDocument failed', err);
      throw err;
    });

    // Per-index slots for buffering out-of-order captures. Under
    // concurrency > 1 page N+1 can complete before page N; we hold completed
    // results here until the head of the queue advances, then flush in order.
    const pendingResults: (PageResult | null)[] = input.pages.map(() => null);
    let nextExpectedIndex = 0;
    let flushChain: Promise<void> = initPromise;

    const flushReady = () => {
      while (nextExpectedIndex < input.pages.length) {
        const next = pendingResults[nextExpectedIndex];
        if (!next) {
          break;
        }
        const flushIndex = nextExpectedIndex;
        const flushResult = next;
        pendingResults[nextExpectedIndex] = null;
        nextExpectedIndex += 1;
        // Chain awaits so addCapturedPage calls are sequential (jspdf isn't
        // safe for concurrent mutations of a single document instance).
        flushChain = flushChain.then(async () => {
          if (controller.signal.aborted) {
            return;
          }
          if (flushResult.status !== 'done' || !flushResult.image) {
            return;
          }
          try {
            await exporter.addCapturedPage(input.pages[flushIndex], flushResult.image, flushResult.footer);
          } catch (err) {
            Logger.error(`PdfExport: addCapturedPage failed for index ${flushIndex}`, err);
          } finally {
            // Drop the Blob reference so the browser can release it.
            flushResult.image = null;
          }
        });
      }
    };

    const handlePageUpdate = (result: PageResult) => {
      setState(prev => {
        const nextPages = prev.pages.slice();
        nextPages[result.index] = result;
        return { ...prev, pages: nextPages };
      });

      // Buffer terminal results (done/skipped/failed/canceled) for in-order
      // flush. Non-terminal updates (pending → loading) do not advance the
      // queue.
      if (
        result.status === 'done' ||
        result.status === 'skipped' ||
        result.status === 'failed' ||
        result.status === 'canceled'
      ) {
        pendingResults[result.index] = result;
        flushReady();
      }
    };

    runCapturePipeline({
      pages: input.pages,
      signal: controller.signal,
      onPageUpdate: handlePageUpdate,
      concurrency: input.concurrency,
    })
      .then(async () => {
        // Wait for any still-pending flush operations, then finalize.
        await flushChain;

        if (controller.signal.aborted) {
          setState(prev => ({
            ...prev,
            status: 'canceled',
            finishedAt: performance.now(),
          }));
          return;
        }

        setState(prev => ({ ...prev, status: 'rendering' }));

        const renderResult = await exporter.finalize(filenameRef.current, true);

        if (renderResult.pdfBlob) {
          blobRef.current = renderResult.pdfBlob;
        }

        setState(prev => ({
          ...prev,
          status: renderResult.success ? 'done' : 'failed',
          finishedAt: performance.now(),
          resultBlob: renderResult.pdfBlob,
          error: renderResult.success ? undefined : renderResult.error,
        }));
      })
      .catch(err => {
        Logger.error('PdfExport pipeline failed', err);
        setState(prev => ({
          ...prev,
          status: 'failed',
          finishedAt: performance.now(),
          error: err instanceof Error ? err.message : 'export failed',
        }));
      });
  }, []);

  const downloadAgain = useCallback(() => {
    if (blobRef.current) {
      triggerBrowserDownload(blobRef.current, filenameRef.current);
    }
  }, []);

  const value = useMemo<PdfExportContextValue>(() => ({ state, start, cancel }), [state, start, cancel]);

  return (
    <PdfExportContext.Provider value={value}>
      {children}
      <PdfExportProgressDialog
        open={dialogOpen}
        state={state}
        onCancel={cancel}
        onClose={closeDialog}
        onDownloadAgain={downloadAgain}
      />
    </PdfExportContext.Provider>
  );
};

export const usePdfExport = (): PdfExportContextValue => {
  const ctx = useContext(PdfExportContext);
  if (!ctx) {
    throw new Error('usePdfExport must be used within a PdfExportProvider');
  }
  return ctx;
};
